import { Badge } from '@crowdfpl/ui-web/badge';
import { Button } from '@crowdfpl/ui-web/button';
import { Input } from '@crowdfpl/ui-web/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@crowdfpl/ui-web/select';
import { ChevronDown, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { trackUmamiEvent } from '@/helpers/analytics';
import { cn } from '@/helpers/utils';
import { usePlayersSearch } from '@/hooks/data/players';
import { useCurrentSeason } from '@/hooks/data/seasons';
import { useTeams } from '@/hooks/data/teams';
import type { PlayerDto } from '@/types/players';

interface PlayerComboboxProps {
  id: string;
  placeholder?: string;
  className?: string;
  value: PlayerDto | null;
  onChange: (p: PlayerDto | null) => void;
  excludePlayer?: PlayerDto | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** One-shot edge trigger: false → true opens the popover; true → false does not close */
  forceOpen?: boolean;
  onSelectMethod?: (method: 'dropdown' | 'search') => void;
}

function getPositionValue(position: string): number | undefined {
  switch (position) {
    case 'GK': return 1;
    case 'DEF': return 2;
    case 'MID': return 3;
    case 'FWD': return 4;
    default: return undefined;
  }
}

const LIMIT = 40;

export function PlayerCombobox({
  id,
  placeholder,
  className,
  value,
  onChange,
  excludePlayer,
  open: controlledOpen,
  onOpenChange,
  forceOpen,
  onSelectMethod,
}: PlayerComboboxProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);
  const prevForceOpenRef = useRef(false);

  // Use controlled or uncontrolled open state
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = useCallback((value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  }, [isControlled, onOpenChange]);

  useEffect(() => {
    const next = !!forceOpen;
    if (next && !prevForceOpenRef.current) {
      setOpen(true);
    }
    prevForceOpenRef.current = next;
  }, [forceOpen, setOpen]);
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const [allPlayers, setAllPlayers] = useState<PlayerDto[]>([]);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Debounce search query to reduce API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Get season for teams
  const { data: seasonData } = useCurrentSeason();
  const { data: teamsData } = useTeams({ season_id: seasonData?.id, limit: 100 });

  const positionNumber = selectedPosition ? getPositionValue(selectedPosition) : undefined;
  const desiredSort = debouncedQuery ? 'total_points.desc' : 'selected_by_percent.desc';

  // Fetch players with current filters
  const { data: searchData, isLoading, isFetching } = usePlayersSearch({
    query: debouncedQuery,
    position: positionNumber,
    team: selectedTeamId || undefined,
    limit: LIMIT,
    offset: 0,
    enabled: open,
    sort: desiredSort,
  });

  // Reset and accumulate players when filters change or when dropdown opens
  useEffect(() => {
    if (searchData?.players && open) {
      setAllPlayers(searchData.players);
      setOffset(LIMIT);
      setShouldLoadMore(false);
    }
  }, [searchData?.players, debouncedQuery, selectedPosition, selectedTeamId, open]);

  // Load more players - only when shouldLoadMore is true
  const { data: loadMoreData } = usePlayersSearch({
    query: debouncedQuery,
    position: positionNumber,
    team: selectedTeamId || undefined,
    limit: LIMIT,
    offset: offset,
    enabled: shouldLoadMore && offset > 0 && !isLoading && (searchData?.pagination.hasMore ?? false),
    sort: desiredSort,
  });

  // Accumulate more players when loadMoreData arrives
  useEffect(() => {
    if (loadMoreData?.players && loadMoreData.players.length > 0) {
      setAllPlayers(prev => {
        // Filter out duplicates by id
        const existingIds = new Set(prev.map(p => p.id));
        const newPlayers = loadMoreData.players.filter(p => !existingIds.has(p.id));
        return [...prev, ...newPlayers];
      });
      setShouldLoadMore(false);
      if (loadMoreData.pagination.hasMore) {
        setOffset(prev => prev + LIMIT);
      } else {
        setOffset(0);
      }
    }
  }, [loadMoreData]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!open || !searchData) return;

    let observer: IntersectionObserver | null = null;

    const timeoutId = setTimeout(() => {
      if (!loadMoreRef.current) return;

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !isFetching && !isLoading && (searchData?.pagination.hasMore ?? false) && !shouldLoadMore) {
            setShouldLoadMore(true);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '100px',
          root: scrollContainerRef.current,
        },
      );

      observer.observe(loadMoreRef.current);
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [open, isFetching, isLoading, searchData?.pagination.hasMore, searchData, shouldLoadMore, allPlayers.length]);

  // Filter out excluded player and ensure no duplicates
  const options = allPlayers
    .filter((p, index, self) => self.findIndex(pl => pl.id === p.id) === index)
    .filter((p) => (excludePlayer ? p.fpl_id !== excludePlayer.fpl_id : true));

  // Reset search and state when dropdown opens
  useEffect(() => {
    if (open) {
      setSearchQuery(''); // Clear search
      setDebouncedQuery(''); // Clear filter to show all players
      setSelectedPosition('');
      setSelectedTeamId('');
      // Focus search input after dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset state when dropdown closes
  useEffect(() => {
    if (!open) {
      setOffset(0);
      setAllPlayers([]);
      setShouldLoadMore(false);
      setSearchQuery('');
      setDebouncedQuery('');
      setSelectedPosition('');
      setSelectedTeamId('');
    }
  }, [open]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !(target as HTMLElement).closest('[role="listbox"]') &&
        !(target as HTMLElement).closest('[data-radix-portal]') &&
        !(target as HTMLElement).closest('[data-radix-select-content]')
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, setOpen]);

  const handleFilterChange = (type: 'position' | 'team', filterValue: string) => {
    if (type === 'position') {
      setSelectedPosition(filterValue);
    } else {
      setSelectedTeamId(filterValue);
    }
  };

  const clearFilters = () => {
    setSelectedPosition('');
    setSelectedTeamId('');
  };

  const hasActiveFilters = !!selectedPosition || !!selectedTeamId;

  return (
    <div className={className} ref={containerRef} data-player-combobox-container>
      <div className="relative">
        <Button
          type="button"
          variant="outline"
          className={cn(
            'w-full h-9 justify-between text-left font-normal',
            !value && 'text-muted-foreground',
          )}
          onClick={() => {
            // Close other dropdowns first
            const allContainers = document.querySelectorAll('[data-player-combobox-container]');
            allContainers.forEach((container) => {
              if (container !== containerRef.current) {
                // Find and close other dropdowns
                const otherButton = container.querySelector('button');
                if (otherButton) {
                  otherButton.blur();
                }
              }
            });
            setOpen(!open);
          }}
        >
          {!value && (
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          )}
          <span className={cn('truncate', !value && 'pl-5')}>
            {value ? value.web_name : placeholder}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {value && (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(null);
                  }
                }}
                className="hover:bg-accent rounded p-0.5 cursor-pointer"
              >
                <X className="h-3 w-3" />
              </div>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
        {open && (
          <div
            ref={dropdownRef}
            className="absolute z-50 bg-popover border mt-1 min-w-[400px] rounded-md shadow max-h-96 flex flex-col"
            onWheel={(e) => {
              // Prevent page scroll when scrolling inside dropdown
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              // Prevent page scroll on mobile when scrolling inside dropdown
              e.stopPropagation();
            }}
          >
            {/* Search input */}
            <div className="p-2 border-b flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  ref={searchInputRef}
                  placeholder="Search players..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                  autoComplete="off"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Filters row */}
            <div className="p-2 border-b space-y-2 flex-shrink-0">
              <div className="grid grid-cols-2 gap-2">
                <Select value={selectedPosition} onValueChange={(v) => handleFilterChange('position', v)}>
                  <SelectTrigger className="h-8 text-xs pl-2 pr-0.5">
                    <SelectValue placeholder="Position" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GK">GK</SelectItem>
                    <SelectItem value="DEF">DEF</SelectItem>
                    <SelectItem value="MID">MID</SelectItem>
                    <SelectItem value="FWD">FWD</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={selectedTeamId} onValueChange={(v) => handleFilterChange('team', v)}>
                  <SelectTrigger className="h-8 text-xs pl-2 pr-0.5">
                    <SelectValue placeholder="Team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamsData?.teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.short_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Active filters badges */}
              {hasActiveFilters && (
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedPosition && (
                    <Badge variant="secondary" className="gap-1 text-xs h-6">
                      {selectedPosition}
                      <X
                        className="h-2 w-2 cursor-pointer"
                        onClick={() => handleFilterChange('position', '')}
                      />
                    </Badge>
                  )}
                  {selectedTeamId && (
                    <Badge variant="secondary" className="gap-1 text-xs h-6">
                      {teamsData?.teams.find(t => t.id === selectedTeamId)?.short_name || 'Team'}
                      <X
                        className="h-2 w-2 cursor-pointer"
                        onClick={() => handleFilterChange('team', '')}
                      />
                    </Badge>
                  )}
                  <button
                    onClick={clearFilters}
                    className="h-6 px-2 text-xs shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Players list with scroll */}
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto"
              onWheel={(e) => {
                // Prevent page scroll when scrolling inside list
                e.stopPropagation();
                const target = e.currentTarget;
                const { scrollTop, scrollHeight, clientHeight } = target;
                const isAtTop = scrollTop === 0;
                const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

                // Prevent scroll if at boundaries
                if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
                  e.preventDefault();
                }
              }}
              onTouchMove={(e) => {
                // Prevent page scroll on mobile
                e.stopPropagation();
              }}
            >
              {isLoading && offset === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
              ) : options.length > 0 ? (
                <>
                  {options.map((p) => (
                    <button
                      key={p.id}
                      className="w-full text-left px-3 py-2 hover:bg-primary/10 hover:border-primary/50 transition-colors border border-transparent"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        trackUmamiEvent('duel_create_player_select', {
                          playerId: p.id,
                          playerNum: id,
                          webName: p.web_name,
                        });
                        // Determine selection method: if user typed a search query, it's 'search', otherwise 'dropdown'
                        const method = searchQuery.trim() ? 'search' : 'dropdown';
                        onSelectMethod?.(method);
                        onChange(p);
                        setOpen(false);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium truncate">{p.web_name}</span>
                          <span className="px-1.5 py-0.5 bg-muted rounded text-xs font-medium shrink-0">
                            {((et) => (et === 1 ? 'GK' : et === 2 ? 'DEF' : et === 3 ? 'MID' : et === 4 ? 'FWD' : ''))(p.element_type)}
                          </span>
                          <span className="text-muted-foreground text-xs shrink-0">{p.team.short_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                          <span>£{((p.now_cost ?? 0) / 10).toFixed(1)}m</span>
                          <span>{p.total_points ?? 0}pts</span>
                          <span>{p.selected_by_percent ?? '0'}%</span>
                        </div>
                      </div>
                    </button>
                  ))}

                  {/* Load more trigger */}
                  {searchData?.pagination.hasMore && (
                    <div ref={loadMoreRef} className="py-4 text-center min-h-[40px]">
                      {isFetching && (
                        <div className="text-sm text-muted-foreground">Loading more...</div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 text-center text-sm text-muted-foreground">No players found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
